import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserGuide {
  id: string;
  title: string;
  description?: string;
  file_url: string;
  file_name: string;
  file_size?: number;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export const useUserGuides = () => {
  return useQuery({
    queryKey: ["user-guides"],
    queryFn: async (): Promise<UserGuide[]> => {
      const { data, error } = await supabase
        .from("user_guides")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
};

export const useUploadUserGuide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      title,
      description,
    }: {
      file: File;
      title: string;
      description?: string;
    }) => {
      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from("user-guides")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("user-guides")
        .getPublicUrl(filePath);

      // Create database record
      const { data, error } = await supabase
        .from("user_guides")
        .insert({
          title,
          description,
          file_url: publicUrl,
          file_name: file.name,
          file_size: file.size,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-guides"] });
      toast({
        title: "Success",
        description: "User guide uploaded successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to upload user guide",
        variant: "destructive",
      });
      console.error("Upload error:", error);
    },
  });
};

export const useDeleteUserGuide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (guide: UserGuide) => {
      // Extract file path from URL
      const url = new URL(guide.file_url);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts[pathParts.length - 1];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("user-guides")
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error } = await supabase
        .from("user_guides")
        .delete()
        .eq("id", guide.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-guides"] });
      toast({
        title: "Success",
        description: "User guide deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error", 
        description: "Failed to delete user guide",
        variant: "destructive",
      });
      console.error("Delete error:", error);
    },
  });
};